import { StrictMode, useCallback, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { PwaProvider } from '@pwaship/react'
import { SkinProvider, StringsProvider, ToastProvider } from '@pwaship/ui'
import '@pwaship/ui/styles.css'

import App from './App'
import './styles/theme.css'
import { skins } from './styles/skin'
import { NS } from './lib/storage'

/**
 * 应用入口。
 *
 * 两个顺序上的要点：
 * 1. `@pwaship/core/install` 在被 import 的那一刻就抓 `beforeinstallprompt`——那个事件
 *    早于 React 挂载，等 effect 就晚了。PwaProvider 内部已经 import 了它，这里不用管。
 * 2. 存储命名空间迁移（如果配了 legacyNamespace）在 PwaProvider 的第一个 useMemo 里跑，
 *    早于任何模块读键。顺序错了，主题/语言会先读到空值再把默认值写回去。
 */

/**
 * 中性化路由：地址栏恒为 '/'，真实路径只活在 `history.state` 里。
 *
 * 威胁模型是"拿到这台已解锁手机的人"——他在地址栏敲一个字母就看见了历史建议里的
 * `/chat/2`，应用锁一次都没被触发。**只有伪装类、隐私类应用该开**：
 * 它换掉的是"URL 可分享 / 可收藏"这个 Web 基本预期。细节见 AGENTS.md §9。
 *
 * 开成 true 之后业务代码**不用改**：App 里已经用 `useNeutralPath()` 挑数据源了。
 */
const NEUTRAL_HISTORY = false

function Root() {
  // 这里用一个最简的"当前路径"状态代替路由库。接你自己的路由时，
  // 把 onNavigate 换成 router.navigate 即可——它同时服务于
  // 「点通知冷启动」和「已有窗口时点通知」两条路径。
  const [path, setPath] = useState(() => window.location.pathname + window.location.search)

  const onNavigate = useCallback((next: string) => {
    // ⚠️ state 里带上 `p`，即使现在没开中性化路由。
    // 中性化靠 `state.p` 还原路径，而空 `p` 的安全默认是"落回首屏"——
    // 现在顺手带上，以后开的时候就不用回来重写这一行（也不用回来查为什么返回键把人踢走了）。
    window.history.pushState({ p: next }, '', next)
    setPath(next)
  }, [])

  // ⚠️ 压了 entry 就必须接住 popstate。不接的话，用户点通知深链进来之后按返回键
  // 什么都不会发生——安卓上这会被当成"应用卡死了"。
  useEffect(() => {
    if (NEUTRAL_HISTORY) return // 开了中性化的话由 useNeutralPath 负责，别两边抢
    const onPop = (e: PopStateEvent) => {
      const p = (e.state as { p?: unknown } | null)?.p
      setPath(typeof p === 'string' ? p : window.location.pathname + window.location.search)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  return (
    <PwaProvider
      config={{
        namespace: NS,
        // 开发时 vite-plugin-pwa 以 ES 模块提供 SW；生产构建成 iife（兼容面更广）
        swType: import.meta.env.DEV ? 'module' : 'classic',
        updateStrategy: 'prompt',
        neutralHistory: NEUTRAL_HISTORY,
      }}
      onNavigate={onNavigate}
    >
      {/*
        皮肤：namespace 必须与 PwaProvider、以及 index.html 里那段防闪脚本三处一致。
        只有一套皮肤时用户仍然能切"跟随系统 / 浅色 / 深色"。
      */}
      <SkinProvider skins={skins} namespace={NS}>
        <StringsProvider strings="zh">
          <ToastProvider>
            <App path={path} navigate={onNavigate} />
          </ToastProvider>
        </StringsProvider>
      </SkinProvider>
    </PwaProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>
)
